import { useFetch } from "@/lib/axios.config.ts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import showCustomToast from "@/shared/utils/showCustomToast.ts";
import { ImageType } from "@/pages/challenges/types/common";

interface UserProfile {
  id: string;
  full_name: string;
  username: string | null;
  phone: string | null;
  birth_date: string | null;
  profile_completion_awarded: boolean;
  gender: "MALE" | "FEMALE" | null;
  avatar: ImageType | null;
  language_code: string;
  wallet_amount: string;
  referral_code: string;
  referral_count: number;
  is_premium: boolean;
  is_active: boolean;
  block_reason: string | null;
  region: {
    id: string;
    name: string;
    soato: string;
  };
  district: {
    id: string;
    name: string;
    soato: string;
    region_id: string;
  };
}

type ConnectAccountStatus = "SUCCESS" | "INVALID_SOURCE" | "INVALID_RESPONSE";
type ConnectAccountState =
  | "CONNECTED"
  | "DON_ACCOUNT_BUSY"
  | "CONNECTED_TO_OTHER_ACCOUNT";
type ConnectAccountResponse = {
  status: ConnectAccountStatus;
  state: ConnectAccountState;
  message: string;
  source?: string;
};

const CONNECTED_ACCOUNT_SOURCE_LABELS: Record<string, string> = {
  hgt: "DOMO",
};

const getConnectedAccountSourceLabel = (source: string) =>
  CONNECTED_ACCOUNT_SOURCE_LABELS[source.toLowerCase()] ?? source;

const useConnectAccount = () => {
  const { t } = useTranslation();
  return useMutation({
    mutationKey: ["connect"],
    mutationFn: async (data: { source: string; key: string }) => {
      const res = await useFetch.post<ConnectAccountResponse>(
        "users/ConnectAccount",
        data
      );

      return {
        ...res.data,
        source: data.source,
      };
    },
    onSuccess(data) {
      let toastMessage = "";

      if (data.status === "INVALID_SOURCE") {
        toastMessage = t("invalid_source");
      } else if (data.status === "INVALID_RESPONSE") {
        toastMessage = t("invalid_response");
      } else if (data.state === "CONNECTED_TO_OTHER_ACCOUNT") {
        // The DOMO account is linked to the user's OTHER Telegram account.
        // We can't switch Telegram accounts for them, so tell them to do it.
        toastMessage =
          t("connect_other_telegram_account") ||
          "Bu hisob boshqa Telegram akkauntingizga ulangan. O'sha akkauntga o'tib qaytadan kiring.";
      } else if (data.state === "DON_ACCOUNT_BUSY") {
        toastMessage = t("don_account_busy");
      } else if (data.state === "CONNECTED" && !!data.source) {
        toastMessage = t("account_connected_successfully", {
          source: getConnectedAccountSourceLabel(data.source),
        });
      }

      if (toastMessage) {
        const isSuccess =
          data.state === "CONNECTED" && data.status === "SUCCESS";
        if (isSuccess) {
          showCustomToast(toastMessage, "success");
        } else {
          showCustomToast(toastMessage, "error");
        }
      }
    },
    onError() {
      showCustomToast(t("invalid_response"), "error");
    },
  });
};

const useGetProfile = (hasToken = true) => {
  const navigate = useNavigate();
  return useQuery({
    queryKey: ["profile"],
    queryFn: async () => {
      const res = await useFetch.get<UserProfile>("users/Profile");
      if (!res.data.full_name) {
        navigate("/auth");
      }
      return res.data;
    },
    staleTime: 30 * 1000,
    enabled: hasToken,
  });
};

interface PremiumInfo {
  id: string;
  months: number;
  price: number;
  percentage: number;
  don_award: string;
}

const usePremiumInfos = () => {
  return useQuery({
    queryKey: ["premiumList"],
    queryFn: async () =>
      (await useFetch.get<PremiumInfo[]>("common/PremiumPurchaseOptions")).data,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};

interface PaymentProvider {
  id: string;
  merchant: string;
  title: string;
  logo: ImageType;
}

const useProviders = () => {
  return useQuery({
    queryKey: ["providers"],
    queryFn: async () =>
      (await useFetch.get<PaymentProvider[]>("payment/PaymentProviderList"))
        .data,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};

const useGetPremium = () => {
  return useMutation({
    mutationKey: ["getPremium"],
    mutationFn: async (data: {
      merchant: any;
      return_url: any;
      premium_option_id: any;
    }) =>
      (await useFetch.get("payment/GetPremiumPayLink", { params: data })).data,
  });
};

const useSetPhone = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["setPhone"],
    mutationFn: async (contact_init_data: string) => {
      const res = await useFetch.post<{ phone: string }>("users/SetPhone", {
        contact_init_data,
      });
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["profile"] });
    },
  });
};

const formatPhoneMasked = (phone: string | null | undefined): string => {
  if (!phone) return "";
  const digits = phone.replace(/\D/g, "");
  if (digits.length < 7) return phone;
  // +998 (90) ••• •• 67
  const country = digits.slice(0, 3);
  const op = digits.slice(3, 5);
  const last = digits.slice(-2);
  return `+${country} (${op}) ••• •• ${last}`;
};

export {
  useGetProfile,
  useConnectAccount,
  usePremiumInfos,
  useProviders,
  useGetPremium,
  useSetPhone,
  formatPhoneMasked,
};
export { useSharePhone } from "./useSharePhone.ts";
